// apps/web/src/views/public-views/result/index.js

import {
  Spin,
} from 'antd'

import {
  useCallback,
  useEffect,
  useState,
} from 'react'

import {
  useNavigate,
  useParams,
} from 'react-router-dom'

import fetch from 'auth/FetchInterceptor'

import {
  OfflineGameService,
  OfflineSpinService,
  OfflineMemoryMatchService,
  OfflineMemorySyncService,
} from 'offline'

import RouletteResult from './games/roulette'
import MemoryResult from './games/memory'

const ResultPage = () => {
  const {
    type,
    id,
    spinId,
  } = useParams()

  const navigate =
    useNavigate()

  const [
    loading,
    setLoading,
  ] = useState(true)

  const [
    data,
    setData,
  ] = useState(null)

  const [
    resultType,
    setResultType,
  ] = useState(null)

  const [
    redirecting,
    setRedirecting,
  ] = useState(false)

  // ======================================================
  // SINCRONIZAR PARTIDAS OFFLINE DA MEMÓRIA
  // ======================================================

  useEffect(() => {
    let mounted =
      true

    const syncMemoryMatches =
      async () => {
        if (
          typeof navigator !==
          'undefined' &&
          navigator.onLine ===
          false
        ) {
          return
        }

        try {
          const result =
            await OfflineMemorySyncService
              .syncPendingMatches()

          if (
            !mounted
          ) {
            return
          }

          if (
            result?.synced >
            0
          ) {
            console.log(
              `[MEMORY SYNC] ${result.synced} partida(s) sincronizada(s).`,
            )
          }
        } catch (
        error
        ) {
          console.warn(
            '[MEMORY SYNC] Sincronização automática não disponível:',
            error,
          )
        }
      }

    /*
     * Tenta sincronizar assim que
     * a página é carregada.
     */
    syncMemoryMatches()

    /*
     * E tenta novamente quando o
     * navegador detectar retorno
     * da internet.
     */
    const handleOnline =
      () => {
        syncMemoryMatches()
      }

    window.addEventListener(
      'online',
      handleOnline,
    )

    return () => {
      mounted =
        false

      window.removeEventListener(
        'online',
        handleOnline,
      )
    }
  }, [])

  // ======================================================
  // CONFIGURAÇÃO DA TELA DE ESPERA
  // ======================================================

  const updateIdleScreenConfig =
    useCallback(
      (game) => {
        if (!game) {
          return
        }

        const idleScreenConfig = {
          gameId:
            game.id,

          idleScreenEnabled:
            game.idleScreenEnabled ===
            true,

          idleScreenDelay:
            Number(
              game.idleScreenDelay ||
              30000,
            ),

          idleScreenShowBackground:
            game.idleScreenShowBackground !==
            false,

          idleScreenBackgroundUrl:
            game.idleScreenBackgroundUrl ||
            '',

          idleScreenShowVideo:
            game.idleScreenShowVideo ===
            true,

          idleScreenVideoUrl:
            game.idleScreenVideoUrl ||
            '',

          idleScreenMessage:
            game.idleScreenMessage ||
            'Toque na tela para começar',

          idleScreenMessageColor:
            game.idleScreenMessageColor ||
            '#ffffff',

          idleScreenMessageFontSize:
            Number(
              game.idleScreenMessageFontSize ||
              48,
            ),

          idleScreenMessagePosition:
            game.idleScreenMessagePosition ||
            'CENTER',

          idleScreenOverlayColor:
            game.idleScreenOverlayColor ||
            '#000000',

          idleScreenOverlayOpacity:
            Number(
              game.idleScreenOverlayOpacity ??
              20,
            ),
        }

        sessionStorage.setItem(
          'games_idle_screen_config',
          JSON.stringify(
            idleScreenConfig,
          ),
        )

        sessionStorage.setItem(
          'games_fullscreen_enabled',
          game.fullscreenEnabled ===
            true
            ? 'true'
            : 'false',
        )

        window.dispatchEvent(
          new CustomEvent(
            'games:idle-config-updated',
          ),
        )
      },
      [],
    )

  // ======================================================
  // RESULTADO OFFLINE DA ROLETA
  // ======================================================

  const loadOfflineRouletteResult =
    useCallback(
      async (
        currentSpinId,
      ) => {
        const localSpin =
          await OfflineSpinService
            .getOfflineSpin(
              currentSpinId,
            )

        if (!localSpin) {
          return null
        }

        const localGame =
          await OfflineGameService
            .getGameById(
              localSpin.gameId,
            )

        if (!localGame) {
          throw new Error(
            'Jogo do resultado offline não encontrado.',
          )
        }

        return {
          id:
            localSpin.localId,

          localId:
            localSpin.localId,

          clientOperationId:
            localSpin
              .clientOperationId,

          game:
            localGame,

          prize:
            localSpin.prize,

          offline:
            true,

          syncStatus:
            localSpin
              .syncStatus,

          createdAt:
            localSpin
              .createdAt,
        }
      },
      [],
    )

  // ======================================================
  // CARREGAR RESULTADO DA ROLETA
  // ======================================================

  const loadRouletteResult =
    useCallback(
      async (
        currentSpinId,
      ) => {
        if (!currentSpinId) {
          return null
        }

        /*
         * Os spins offline possuem ID começando
         * normalmente com "spin_".
         */
        if (
          String(
            currentSpinId,
          ).startsWith(
            'spin_',
          )
        ) {
          console.log(
            '[RESULT][ROULETTE] Resultado offline:',
            currentSpinId,
          )

          return loadOfflineRouletteResult(
            currentSpinId,
          )
        }

        /*
         * Primeiro tenta carregar da API.
         */
        try {
          const result =
            await fetch({
              url:
                `/spins/public-result/${currentSpinId}`,

              method:
                'get',

              silentNetworkError:
                true,
            })

          console.log(
            '[RESULT][ROULETTE] Resultado online:',
            result,
          )

          return result
        } catch (error) {
          console.warn(
            '[RESULT][ROULETTE] API indisponível. Tentando IndexedDB...',
            error,
          )

          return loadOfflineRouletteResult(
            currentSpinId,
          )
        }
      },
      [
        loadOfflineRouletteResult,
      ],
    )

  // ======================================================
  // RESULTADO OFFLINE DO MEMORY
  // ======================================================

  const loadOfflineMemoryResult =
    useCallback(
      async (
        matchId,
      ) => {
        if (!matchId) {
          return null
        }

        const localMatch =
          await OfflineMemoryMatchService
            .getMatchById(
              matchId,
            )

        if (!localMatch) {
          return null
        }

        const localGame =
          await OfflineGameService
            .getGameById(
              localMatch.gameId,
            )

        if (!localGame) {
          throw new Error(
            'Jogo da partida offline não encontrado.',
          )
        }

        return {
          ...localMatch,

          id:
            localMatch.id ||
            localMatch.localId,

          localId:
            localMatch.localId ||
            localMatch.id,

          game:
            localGame,

          participant:
            localMatch.participant ||
            null,

          completed:
            localMatch.completed ===
            true,

          durationMs:
            Number(
              localMatch.durationMs ||
              0,
            ),

          attempts:
            Number(
              localMatch.attempts ||
              0,
            ),

          errors:
            Number(
              localMatch.errors ||
              0,
            ),

          score:
            Number(
              localMatch.score ||
              0,
            ),

          offline:
            true,

          syncStatus:
            localMatch.syncStatus ||
            'PENDING',
        }
      },
      [],
    )

  // ======================================================
  // CARREGAR RESULTADO DO MEMORY
  // ======================================================

  const loadMemoryResult =
    useCallback(
      async (
        matchId,
      ) => {
        if (!matchId) {
          return null
        }

        /*
         * ================================================
         * ID GERADO OFFLINE
         * ================================================
         */

        if (
          String(
            matchId,
          ).startsWith(
            'offline-memory-',
          )
        ) {
          console.log(
            '[RESULT][MEMORY] Resultado offline:',
            matchId,
          )

          return loadOfflineMemoryResult(
            matchId,
          )
        }

        /*
         * ================================================
         * TENTAR API
         * ================================================
         */

        try {
          const result =
            await fetch({
              url:
                `/memory-games/matches/${matchId}`,

              method:
                'get',

              silentNetworkError:
                true,
            })

          console.log(
            '[RESULT][MEMORY] Resultado online:',
            result,
          )

          return result
        } catch (
        error
        ) {
          console.warn(
            '[RESULT][MEMORY] API indisponível. Tentando armazenamento offline...',
            error,
          )

          /*
           * Também tentamos localmente mesmo
           * sem o prefixo, para manter compatibilidade
           * com possíveis registros anteriores.
           */

          return loadOfflineMemoryResult(
            matchId,
          )
        }
      },
      [
        loadOfflineMemoryResult,
      ],
    )

  // ======================================================
  // CARREGAR RESULTADO
  // ======================================================

  useEffect(() => {
    let mounted =
      true

    const load =
      async () => {
        try {
          setLoading(
            true,
          )

          setData(
            null,
          )

          setResultType(
            null,
          )

          let result =
            null

          let resolvedType =
            null

          // ==============================================
          // ROTA ANTIGA DA ROLETA
          //
          // /p/:spinId
          // ==============================================

          if (spinId) {
            resolvedType =
              'roulette'

            result =
              await loadRouletteResult(
                spinId,
              )
          }

          // ==============================================
          // ROTA GENÉRICA
          //
          // /r/:type/:id
          // ==============================================

          else if (
            type ===
            'memory'
          ) {
            resolvedType =
              'memory'

            result =
              await loadMemoryResult(
                id,
              )
          }

          // ==============================================
          // FUTURO:
          //
          // /r/roulette/:id
          // ==============================================

          else if (
            type ===
            'roulette'
          ) {
            resolvedType =
              'roulette'

            result =
              await loadRouletteResult(
                id,
              )
          }

          if (!mounted) {
            return
          }

          if (!result) {
            setData(
              null,
            )

            setResultType(
              resolvedType,
            )

            return
          }

          setResultType(
            resolvedType,
          )

          setData(
            result,
          )

          if (
            result?.game
          ) {
            updateIdleScreenConfig(
              result.game,
            )
          }
        } catch (error) {
          console.error(
            '[RESULT] Erro ao carregar resultado:',
            error,
          )

          if (mounted) {
            setData(
              null,
            )
          }
        } finally {
          if (mounted) {
            setLoading(
              false,
            )
          }
        }
      }

    load()

    return () => {
      mounted =
        false
    }
  }, [
    type,
    id,
    spinId,
    loadMemoryResult,
    loadRouletteResult,
    updateIdleScreenConfig,
  ])

  // ======================================================
  // BLOQUEAR BOTÃO DIREITO
  // ======================================================

  useEffect(() => {
    if (
      !data?.game ||
      data.game
        .disableContextMenu !==
      true
    ) {
      return undefined
    }

    const handleContextMenu =
      (event) => {
        event.preventDefault()
      }

    document.addEventListener(
      'contextmenu',
      handleContextMenu,
    )

    return () => {
      document.removeEventListener(
        'contextmenu',
        handleContextMenu,
      )
    }
  }, [
    data,
  ])

  // ======================================================
  // CONTINUAR ROLETA
  // ======================================================

  const handleRouletteContinue =
    useCallback(
      () => {
        if (
          !data?.game ||
          redirecting
        ) {
          return
        }

        const game =
          data.game

        setRedirecting(
          true,
        )

        const shouldShowAfterForm =
          game.leadCapturePosition ===
          'AFTER_GAME' &&
          sessionStorage.getItem(
            `game_${game.id}_show_lead_after`,
          ) ===
          'true'

        // ================================================
        // FORMULÁRIO APÓS O JOGO
        // ================================================

        if (
          shouldShowAfterForm
        ) {
          navigate(
            `/play/${game.slug}`,
          )

          return
        }

        // ================================================
        // REDIRECIONAMENTO EXTERNO PELO BOTÃO
        // ================================================

        const externalRedirectEnabled =
          game.externalRedirectEnabled ===
          true &&
          Boolean(
            game.externalRedirectUrl,
          )

        const externalRedirectMode =
          String(
            game.externalRedirectMode ||
            'BUTTON',
          ).toUpperCase()

        if (
          externalRedirectEnabled &&
          externalRedirectMode ===
          'BUTTON'
        ) {
          localStorage.removeItem(
            `game_${game.id}_lead_id`,
          )

          localStorage.removeItem(
            `game_${game.id}_lead_values`,
          )

          sessionStorage.removeItem(
            `game_${game.id}_show_lead_after`,
          )

          sessionStorage.removeItem(
            `game_${game.id}_last_spin_offline`,
          )

          sessionStorage.removeItem(
            `game_${game.id}_last_spin_id`,
          )

          window.location.href =
            game.externalRedirectUrl

          return
        }

        // ================================================
        // LIMPAR PARTICIPANTE
        // ================================================

        localStorage.removeItem(
          `game_${game.id}_lead_id`,
        )

        localStorage.removeItem(
          `game_${game.id}_lead_values`,
        )

        sessionStorage.removeItem(
          `game_${game.id}_show_lead_after`,
        )

        sessionStorage.removeItem(
          `game_${game.id}_last_spin_offline`,
        )

        sessionStorage.removeItem(
          `game_${game.id}_last_spin_id`,
        )

        navigate(
          `/play/${game.slug}`,
        )
      },
      [
        data,
        redirecting,
        navigate,
      ],
    )

  // ======================================================
  // CONTINUAR MEMORY
  // ======================================================

  const handleMemoryContinue =
    useCallback(
      () => {
        if (
          !data?.game ||
          redirecting
        ) {
          return
        }

        const game =
          data.game

        setRedirecting(
          true,
        )

        const shouldShowAfterForm =
          game.leadCapturePosition ===
          'AFTER_GAME' &&
          sessionStorage.getItem(
            `game_${game.id}_show_lead_after`,
          ) ===
          'true'

        if (
          shouldShowAfterForm
        ) {
          navigate(
            `/play/${game.slug}`,
          )

          return
        }

        localStorage.removeItem(
          `game_${game.id}_lead_id`,
        )

        localStorage.removeItem(
          `game_${game.id}_lead_values`,
        )

        sessionStorage.removeItem(
          `game_${game.id}_show_lead_after`,
        )

        navigate(
          `/play/${game.slug}`,
        )
      },
      [
        data,
        redirecting,
        navigate,
      ],
    )

  // ======================================================
// RETORNO AUTOMÁTICO DA ROLETA
// ======================================================

useEffect(() => {
  if (
    resultType !==
      'roulette' ||
    !data?.game
  ) {
    return undefined
  }

  const game =
    data.game

  /*
   * ====================================================
   * REDIRECIONAMENTO EXTERNO
   * ====================================================
   */

  const externalRedirectEnabled =
    game.externalRedirectEnabled ===
      true &&
    Boolean(
      game.externalRedirectUrl,
    )

  const externalRedirectMode =
    String(
      game.externalRedirectMode ||
        'BUTTON',
    ).toUpperCase()

  /*
   * ====================================================
   * DECIDE SE EXISTE RETORNO AUTOMÁTICO
   * ====================================================
   *
   * Existem duas possibilidades:
   *
   * 1. prizePageAutoReturn = true
   *    → comportamento normal da roleta
   *
   * 2. externalRedirectMode = AUTO
   *    → redirecionamento externo automático
   */

  const shouldAutoReturn =
    game.prizePageAutoReturn !==
      false ||
    (
      externalRedirectEnabled &&
      externalRedirectMode ===
        'AUTO'
    )

  if (!shouldAutoReturn) {
    return undefined
  }

  const delay =
    Number(
      game.prizePageReturnDelay ||
        8000,
    )

  const timer =
    window.setTimeout(
      () => {
        /*
         * ==============================================
         * REDIRECIONAMENTO EXTERNO AUTOMÁTICO
         * ==============================================
         */

        if (
          externalRedirectEnabled &&
          externalRedirectMode ===
            'AUTO'
        ) {
          /*
           * Limpa os dados do participante atual.
           */

          localStorage.removeItem(
            `game_${game.id}_lead_id`,
          )

          localStorage.removeItem(
            `game_${game.id}_lead_values`,
          )

          sessionStorage.removeItem(
            `game_${game.id}_show_lead_after`,
          )

          sessionStorage.removeItem(
            `game_${game.id}_last_spin_offline`,
          )

          sessionStorage.removeItem(
            `game_${game.id}_last_spin_id`,
          )

          window.location.href =
            game.externalRedirectUrl

          return
        }

        /*
         * ==============================================
         * COMPORTAMENTO NORMAL
         * ==============================================
         */

        handleRouletteContinue()
      },
      delay,
    )

  return () => {
    window.clearTimeout(
      timer,
    )
  }
}, [
  resultType,
  data,
  handleRouletteContinue,
])

  // ======================================================
  // LOADING
  // ======================================================

  if (loading) {
    return (
      <div
        style={{
          width:
            '100vw',

          minHeight:
            '100vh',

          display:
            'grid',

          placeItems:
            'center',
        }}
      >
        <Spin
          size="large"
        />
      </div>
    )
  }

  // ======================================================
  // NÃO ENCONTRADO
  // ======================================================

  if (!data) {
    return (
      <div
        style={{
          width:
            '100vw',

          minHeight:
            '100vh',

          display:
            'grid',

          placeItems:
            'center',

          textAlign:
            'center',

          padding:
            24,

          boxSizing:
            'border-box',
        }}
      >
        <div>
          <h1>
            Resultado não encontrado
          </h1>

          <p>
            Não foi possível carregar esta participação.
          </p>
        </div>
      </div>
    )
  }

  // ======================================================
  // DISPATCHER
  // ======================================================

  switch (
  resultType
  ) {
    // ====================================================
    // ROLETA
    // ====================================================

    case 'roulette': {
      const game =
        data.game

      const prize =
        data.prize || {}

      const hasAfterForm =
        game?.leadCapturePosition ===
        'AFTER_GAME' &&
        sessionStorage.getItem(
          `game_${game.id}_show_lead_after`,
        ) ===
        'true'

      return (
        <RouletteResult
          game={
            game
          }
          prize={
            prize
          }
          redirecting={
            redirecting
          }
          hasAfterForm={
            hasAfterForm
          }
          onContinue={
            handleRouletteContinue
          }
        />
      )
    }

    // ====================================================
    // MEMORY
    // ====================================================

    case 'memory':
      return (
        <MemoryResult
          game={
            data.game
          }
          match={
            data
          }
          redirecting={
            redirecting
          }
          onContinue={
            handleMemoryContinue
          }
        />
      )

    // ====================================================
    // NÃO SUPORTADO
    // ====================================================

    default:
      return (
        <div
          style={{
            width:
              '100vw',

            minHeight:
              '100vh',

            display:
              'grid',

            placeItems:
              'center',

            textAlign:
              'center',
          }}
        >
          <div>
            <h1>
              Resultado não suportado
            </h1>

            <p>
              Tipo:{' '}
              {String(
                resultType ||
                type ||
                '',
              )}
            </p>
          </div>
        </div>
      )
  }
}

export default ResultPage